import { apiClient } from './apiClient';
import type { SupportCategory } from './supportService';

export type TicketStatus =
  | 'not_started'
  | 'in_progress'
  | 'blocked'
  | 'in_review'
  | 'done';
export type TicketCategory = SupportCategory;
export type TicketPriority = 'low' | 'medium' | 'high';

export interface Ticket {
  id: string;
  tenant_id: string;
  source: string;
  category: TicketCategory;
  team: string;
  title: string;
  description: string;
  status: TicketStatus;
  priority: TicketPriority;
  points: number;
  assignee?: string | null;
  assignee_name?: string | null;
  requested_by?: string | null;
  page_url?: string | null;
  details?: { teamwork?: { ticket_id?: string; ticket_url?: string } } | null;
  created_at: string;
  updated_at: string;
}

export interface TicketActivity {
  id: string;
  action: string;
  actor_email?: string | null;
  old_status?: string | null;
  new_status?: string | null;
  detail?: string | null;
  created_at: string;
}

export interface TicketDetail extends Ticket {
  activity: TicketActivity[];
}

export interface TicketListResponse {
  items: Ticket[];
  total: number;
}

export type TicketViewScope = 'all' | 'department' | 'tenant';

export interface MyTicketListResponse extends TicketListResponse {
  scope: TicketViewScope;
}

export interface TicketExpert {
  id: string;
  name: string;
  [key: string]: unknown;
}

export interface CreateTicketInput {
  category: TicketCategory;
  description: string;
  title?: string;
  priority?: TicketPriority;
  page_url?: string;
}

export interface TicketDraftMessage {
  role: 'user' | 'assistant';
  content: string;
}

export type TicketDraftResult =
  | { type: 'question'; question: string }
  | {
      type: 'draft';
      title: string;
      description: string;
      category: TicketCategory;
      priority: TicketPriority;
    }
  | { type: 'manual'; message: string; prefill_description: string };

export const ticketsService = {
  async list(
    tenantId: string,
    params?: { status?: string; assignee?: string },
  ): Promise<TicketListResponse> {
    const { data } = await apiClient.get<TicketListResponse>('/tickets', {
      params: { tenant_id: tenantId, ...(params ?? {}) },
    });
    return data;
  },

  async get(id: string): Promise<TicketDetail> {
    const { data } = await apiClient.get<TicketDetail>(`/tickets/${id}`);
    return data;
  },

  async listMine(tenantId?: string | null): Promise<MyTicketListResponse> {
    const { data } = await apiClient.get<MyTicketListResponse>('/tickets/mine', {
      params: tenantId ? { tenant_id: tenantId } : {},
    });
    return data;
  },

  async create(tenantId: string, body: CreateTicketInput): Promise<Ticket> {
    const { data } = await apiClient.post<Ticket>('/tickets', body, {
      params: { tenant_id: tenantId },
    });
    return data;
  },

  async setStatus(id: string, status: TicketStatus): Promise<Ticket> {
    const { data } = await apiClient.post<Ticket>(`/tickets/${id}/status`, { status });
    return data;
  },

  async assign(id: string, assignee: string): Promise<Ticket> {
    const { data } = await apiClient.post<Ticket>(`/tickets/${id}/assign`, { assignee });
    return data;
  },

  async experts(): Promise<TicketExpert[]> {
    const { data } = await apiClient.get<{ experts: TicketExpert[] }>('/tickets/experts');
    return data.experts ?? [];
  },

  async draftFromConversation(messages: TicketDraftMessage[]): Promise<TicketDraftResult> {
    const { data } = await apiClient.post<TicketDraftResult>('/tickets/ai-draft', { messages });
    return data;
  },
};
